import { makeAutoObservable } from "mobx";
import { chatSession, RunSummary, SessionMessage } from "@/aawebsocket/chatSession";
import { chatState } from "./chatState";

export type RunEntry = RunSummary & { finishedUnseen: boolean };

const scopeKey = (tagIds: number[]) => [...tagIds].sort((a, b) => a - b).join("-");

/** Every chat run of the user in the active organisation, keyed by conversation id. */
class RunsState {
    runs = new Map<number, RunEntry>();

    constructor() {
        makeAutoObservable(this);
        chatSession.addListener(this.onMessage);
    }

    onMessage = (message: SessionMessage) => {
        switch (message.type) {
            case "runs": {
                const active = new Set(message.runs.map((r) => r.conversation_id));
                for (const [conversationId, entry] of this.runs) {
                    if (entry.status === "running" && !active.has(conversationId)) this.runs.delete(conversationId);
                }
                message.runs.forEach((run) => this.runs.set(run.conversation_id, { ...run, finishedUnseen: false }));
                break;
            }
            case "run_status": {
                const { type, ...run } = message;
                const watching = run.conversation_id === chatState.conversationId && chatState.mounted;
                this.runs.set(run.conversation_id, { ...run, finishedUnseen: run.status !== "running" && !watching });
                break;
            }
        }
    };

    get list(): RunEntry[] {
        return [...this.runs.values()];
    }

    /** Running, or finished while the user was looking elsewhere. */
    get notable(): RunEntry[] {
        return this.list.filter((r) => r.status === "running" || r.finishedUnseen);
    }

    isRunning(conversationId: number | null | undefined): boolean {
        if (conversationId == null) return false;
        return this.runs.get(conversationId)?.status === "running";
    }

    forScope(tagIds: number[]): RunEntry | undefined {
        const key = scopeKey(tagIds);
        return this.list.find((r) => scopeKey(r.tag_ids) === key);
    }

    /** The most notable run of any conversation that includes this tag: running, then failed, then done. */
    forTag(tagId: number): RunEntry | undefined {
        const rank = (r: RunEntry) => (r.status === "running" ? 0 : r.status === "failed" ? 1 : 2);
        return this.notable.filter((r) => r.tag_ids.includes(tagId)).sort((a, b) => rank(a) - rank(b))[0];
    }

    markSeen(conversationId: number | null | undefined) {
        if (conversationId == null) return;
        const entry = this.runs.get(conversationId);
        if (!entry) return;
        if (entry.status === "running") return;
        this.runs.delete(conversationId);
    }

    clear() {
        this.runs.clear();
    }
}

export const runsState = new RunsState();
